import { useState, useEffect, useCallback, useMemo } from 'react';
import { useApi } from './useApi';

const STORAGE_KEY = 'contador_active_company_id';

export function useContadorScope() {
  const { secureFetch } = useApi();
  const [clients, setClients] = useState([]);
  const [multiClient, setMultiClient] = useState(false);
  const [loadingClients, setLoadingClients] = useState(true);
  const [activeCompanyId, setActiveCompanyIdState] = useState(() => {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem(STORAGE_KEY) || '';
  });

  const loadClients = useCallback(async () => {
    setLoadingClients(true);
    try {
      const res = await secureFetch('/api/contador/clientes');
      if (!res.ok) {
        setClients([]);
        setMultiClient(false);
        return;
      }
      const data = await res.json();
      const list = Array.isArray(data.clients) ? data.clients : [];
      setClients(list);
      setMultiClient(!!data.multiClient);

      const ids = list.map((c) => c.companyId);
      const stored = localStorage.getItem(STORAGE_KEY) || '';
      const next = ids.includes(stored) ? stored : (ids[0] || '');
      setActiveCompanyIdState(next);
      if (next) localStorage.setItem(STORAGE_KEY, next);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      setClients([]);
      setMultiClient(false);
    } finally {
      setLoadingClients(false);
    }
  }, [secureFetch]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const setActiveCompanyId = useCallback((id) => {
    setActiveCompanyIdState(id);
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  }, []);

  const scopedFetch = useCallback(
    (url, options = {}) => {
      if (!activeCompanyId) return secureFetch(url, options);
      const headers = { ...(options.headers || {}) };
      headers['X-Active-Company-Id'] = activeCompanyId;
      return secureFetch(url, { ...options, headers });
    },
    [secureFetch, activeCompanyId]
  );

  const activeClient = useMemo(
    () => clients.find((c) => c.companyId === activeCompanyId) || clients[0] || null,
    [clients, activeCompanyId]
  );

  const isViewerPortal = clients.length > 0;

  return {
    clients,
    multiClient,
    loadingClients,
    activeCompanyId,
    setActiveCompanyId,
    activeClient,
    scopedFetch,
    loadClients,
    isViewerPortal,
  };
}
